<script setup lang="ts">
import merge from 'lodash/merge';

interface FormSetInOrder
{
    content: {
        value: {
            description: string;
            orientation: boolean;
            question: string;
            answers: Answer[];
        };
    };
}

interface Answer
{
    id: number;
    text: string;
}

interface Props
{
    modelValue: any;
}

const props = defineProps<Props>();
const emit = defineEmits<{
    'update:modelValue': [value: any];
}>();

const defaults: any = {
    description: '',
    orientation: 'horizontal',
    question: '',
    answers: [] as Answer[]
};

const form = computed((): FormType<FormSetInOrder> =>
{
    return props.modelValue;
});

onBeforeMount(() =>
{
    form.value.content.value = merge(defaults, form.value.content.value);
    form.value.file = null;

    if (!form.value.content.value.answers.length)
    {
        form.value.content.value.answers = [{
            id: 1,
            text: ''
        }];
    }
});

function addAnswer(): void
{
    const ids = form.value.content.value.answers.map((x: { id: any }) => x.id);

    const maxId = form.value.content.value.answers.length > 0 ? Math.max(...ids) : 1;

    form.value.content.value.answers.push({
        id: maxId + 1,
        text: ''
    });
}

function removeAnswer(index: number): void
{
    const items = form.value.content.value.answers.slice();

    items.splice(index, 1);
    form.value.content.value.answers = items;

    form.value.$errors.clear(`content.value.answers[${index}]`);
}

watch(() => form.value.content.value, (value: any) =>
{
    triggetModel();
}, { deep: true });

function triggetModel(): any
{
    emit('update:modelValue', form.value);
}

function answerUp(index: number): void
{
    if (index > 0)
    {
        const answers = form.value.content.value.answers.splice(index, 1);

        form.value.content.value.answers.splice(index - 1, 0, answers[0]);

        form.value.$errors.clear();
    }
}

function answerDown(index: number): void
{
    if (index < form.value.content.value.answers.length - 1)
    {
        const answers = form.value.content.value.answers.splice(index, 1);

        form.value.content.value.answers.splice(index + 1, 0, answers[0]);

        form.value.$errors.clear();
    }
}
</script>

<template>
    <IdeoFormGroup :label="$t('[[[Opis]]]')" :invalid-feedback="form.$errors.first('content.value.description')" :state="form.$errors.state('content.value.description')">
        <RichTextEditor
            :id="seconds('description-' + form.id)"
            v-model="form.content.value.description"
            name="description"
            shortcode
            timeline
        />
    </IdeoFormGroup>
    <IdeoFormGroup>
        <IdeoFormRadioGroup stacked>
            <IdeoFormRadio v-model="form.content.value.orientation" value="horizontal">
                {{ $t('[[[Poziomo]]]') }}
            </IdeoFormRadio>
            <IdeoFormRadio v-model="form.content.value.orientation" value="vertical">
                {{ $t('[[[Pionowo]]]') }}
            </IdeoFormRadio>
        </IdeoFormRadioGroup>
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Pytanie i odpowiedzi]]]')" :invalid-feedback="form.$errors.first('content.value.questions')" :state="form.$errors.state('content.value.questions')" required>
        <div v-for="(answer, j) in form.content.value.answers" :key="j" class="row g-0 mb-3">
            <div class="col-md-11 col-sm-10">
                <div class="input-group">
                    <div class="input-group-prepend">
                        <span class="input-group-text bg-primary border-primary text-white">{{ 1 }}.{{ j + 1 }}</span>
                    </div>
                    <IdeoFormTextarea v-model="answer.text" :name="`content.value.answers[${j}]`" :rows="2" :placeholder="$t('[[[Odpowiedź]]]')" :data-cy="`answer-${j}`" />
                    <div class="input-group-append mx-1">
                        <ActionButton
                            variant="danger"
                            icon="far fa-trash"
                            :title="$t('[[[Usuń odpowiedź]]]')"
                            :confirm="$t('[[[Potwierdzenie usunięcia]]]')"
                            :disabled="form.content.value.answers.length < 2"
                            @click="removeAnswer(j)"
                        />
                    </div>
                </div>
                <small v-if="!form.$errors.state(`content.value.answers[${j}]`)" class="form-text text-danger">{{ form.$errors.first(`content.value.answers[${j}]`) }}</small>
            </div>
            <div class="col-md-1 col-sm-2 d-flex align-items-start">
                <ActionButton
                    variant="secondary"
                    icon="far fa-arrow-up"
                    class="ms-auto"
                    :title="$t('[[[Wyżej]]]') "
                    :disabled="j == 0"
                    @click="answerUp(j)"
                />
                <ActionButton variant="secondary" icon="far fa-arrow-down" :title="$t('[[[Niżej]]]')" :disabled="j == form.content.value.answers.length - 1" @click="answerDown(j)" />
            </div>
        </div>
        <small v-if="!form.$errors.state(`content.value.answers`)" class="form-text text-danger">{{ form.$errors.first(`content.value.answers`) }}</small>
        <div>
            <ActionButton
                variant="outline-dark"
                class="w-100"
                icon="far fa-plus"
                :text="$t('[[[Dodaj odpowiedź]]]')"
                data-cy="addAnswer"
                @click="addAnswer()"
            />
        </div>
    </IdeoFormGroup>
</template>
